import type { AxiosResponse } from 'axios'
import { defineStore } from 'pinia'
import type {
  ContainerType,
  ExportBookingState,
  ExportCargoType,
  ExportSortOptions,
  GETExport,
  GETExportBookingCheck,
  GetExportsExportsGetSortOrderEnum,
  POSTWatchExportsResponse,
  ShippingLine,
  TerminalName,
} from '~/services/apiClient'
import type { ExportBooking } from '~/models/exports'
import { parseExports } from '~/models/exports'
import { isRequestAbort } from '~/requestAbort'
import { ExportsApi } from '~/services/apiClient'
import { identifierListNotification } from '~/logic/identifierListNotification'
import Notification from '~/services/notificationService'
import { useUserStore } from '~/stores/user'

export interface ExportListQuery {
  sort_by?: ExportSortOptions
  sort_order?: GetExportsExportsGetSortOrderEnum
  booking_number?: string
  terminal?: TerminalName[]
  container_type?: ContainerType
  cargo_types?: ExportCargoType[]
  shipping_line?: ShippingLine
  holds?: string[]
  /** The tab bar's selection (DEV-8135) — `undefined` is the "All" tab. */
  status?: ExportBookingState
  excluded_customer_ids?: number[]
  assigned_user_ids?: number[]
  erd_from?: string
  erd_to?: string
  cutoff_from?: string
  cutoff_to?: string
  tags?: string[]
  is_untagged?: boolean
}

/**
 * Same two bites as the container list. The API defaults to 25, which would
 * hide the rest. One page is 1000. 40 paints first. If that answer is full,
 * the next ask skips those 40 and takes the other 960. The next page starts
 * at 1000, then 2000, and so on.
 */
export const EXPORT_LIST_PAGE_SIZE = 1000
const EXPORT_LIST_PRELOAD_SIZE = 40

/**
 * The most bookings one `POST /exports/bookings/check/` call accepts, and the
 * reason the Add bookings table chunks: 33 is one eModal page, so a chunk that
 * misses the cache costs exactly one portal lookup.
 */
export const BOOKING_CHECK_CHUNK_SIZE = 33

/** One row of the Add bookings table, on its way to `POST /exports/watch/`. */
export interface BookingToWatch {
  bookingNumber: string
  /** Falls back to the request-level customer when unset. */
  customerId?: number
}

export const useExportsStore = defineStore('exports', {
  state: () => {
    return {
      loading: false,
      /**
       * True until the first 40 rows land. The table spinner uses this, not
       * `loading`, so the rest of the page can paint while the other 960 load.
       */
      preLoading: false,
      /** How many bookings match, before this page is cut. Page buttons use it. */
      total: 0,
      /** Bumps on each `load` so a slow response cannot overwrite a newer sort. */
      loadGeneration: 0,
      loadAbortController: null as AbortController | null,
      /**
       * The last `load` failed. The list shows an error with a retry rather
       * than an empty table: the rows are whatever the server last sent, so
       * on a first load there are none and "nothing tracked yet" would be a
       * lie told to someone whose request 500'd.
       */
      loadFailed: false,
      executingWatch: false,
      executingUnwatch: false,
      exports: [] as ExportBooking[],
      exportLookup: new Map() as Map<string, ExportBooking>,
      /** Per-tab booking counts for the status tab bar (DEV-8135), keyed by state. */
      statusCounts: new Map() as Map<ExportBookingState, number>,
      loadStateCountsAbortController: null as AbortController | null,
      /**
       * Cancels the in-flight pre-add check when a newer one starts.
       *
       * `markRaw`'d on the way in: nothing renders it, so there is nothing to
       * gain from making a native object with internal slots reactive, and
       * `signal` is read off the local controller rather than back out of
       * state so a proxied signal never reaches axios.
       */
      checkAbortController: null as AbortController | null,
    }
  },
  actions: {
    cancelExistingRequests(): AbortController {
      this.loadAbortController?.abort()
      const abortController = new AbortController()
      this.loadAbortController = abortController
      return abortController
    },
    /**
     * Per-status booking counts for the tab bar (DEV-8135).
     *
     * Takes every active list filter *except* `status`/`sort_by`/`sort_order` —
     * a tab's own count must stay independent of which tab is currently
     * selected, or picking "Not Receiving" would zero out every other tab's
     * count. Same contract as `containers.ts` `loadStateCounts`.
     */
    loadStateCounts(
      query: Omit<ExportListQuery, 'status' | 'sort_by' | 'sort_order'> = {}
    ): Promise<void> {
      this.loadStateCountsAbortController?.abort()
      const abortController = new AbortController()
      this.loadStateCountsAbortController = abortController
      const api = new ExportsApi()
      return api
        .getExportCountsByStateExportsStateCountsGet(
          query.booking_number,
          query.terminal,
          undefined,
          query.shipping_line ? [query.shipping_line] : undefined,
          query.container_type ? [query.container_type] : undefined,
          query.cargo_types,
          query.holds,
          query.tags,
          // false would still be sent; omit it so an off switch is no param.
          query.is_untagged || undefined,
          // status: the *liveness* filter, a different concept from the tab
          // bar's lifecycle state (DEV-8357's notes). lifecycle_state is
          // deliberately omitted too - every tab's count must stay
          // independent of which tab is currently selected.
          undefined,
          undefined,
          // port_complex: no filter on the list offers this yet.
          undefined,
          query.erd_from,
          query.erd_to,
          query.cutoff_from,
          query.cutoff_to,
          query.excluded_customer_ids,
          query.assigned_user_ids,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          { signal: abortController.signal }
        )
        .then((resp) => {
          this.statusCounts = new Map(resp.data.map((x) => [x.state, x.count]))
        })
        .finally(() => {
          if (this.loadStateCountsAbortController === abortController) {
            this.loadStateCountsAbortController = null
          }
        })
    },
    load(query: ExportListQuery = {}, page = 1): Promise<void> {
      const abortController = this.cancelExistingRequests()
      const generation = ++this.loadGeneration
      this.preLoading = true
      this.loading = true
      this.loadFailed = false
      // Deliberately NOT clearing the rows here, unlike Imports `clearAllData`.
      // The first bite replaces them wholesale a moment later, and the table is
      // under a `preLoading` overlay until it does, so blanking buys nothing —
      // while an empty list un-resolves the split pane's selection (selectedRow
      // derives from these rows) and makes a failed load read as "nothing
      // tracked yet". Page 2 therefore shows page 1 greyed out for a moment
      // instead of an empty table.
      const pageStart = (Math.max(page, 1) - 1) * EXPORT_LIST_PAGE_SIZE
      const api = new ExportsApi()
      const apply = (bookings: ExportBooking[], replace: boolean) => {
        const next = replace ? bookings : this.exports.concat(bookings)
        this.exports = next
        this.exportLookup = new Map(
          next.map((booking) => [booking.booking_number, booking])
        )
      }
      const fetchPage = (
        offset: number,
        pageSize: number,
        includeTotal: boolean
      ) =>
        api
          .getExportsExportsGet(
            query.booking_number,
            query.terminal,
            // customer_ids: the list hides customers rather than keeping them,
            // which is `excluded_customer_ids` below.
            undefined,
            query.shipping_line ? [query.shipping_line] : undefined,
            query.container_type ? [query.container_type] : undefined,
            query.cargo_types,
            query.holds,
            query.tags,
            // false would still be sent; omit it so an off switch is no param.
            query.is_untagged || undefined,
            // status: the *liveness* filter - never the tab bar's lifecycle
            // value, which is sent below as `lifecycle_state`.
            undefined,
            // The tab bar's selection (DEV-8135), now a real argument since
            // DEV-8357 added `lifecycle_state` to this endpoint.
            query.status ? [query.status] : undefined,
            // port_complex: no filter on the list offers this yet.
            undefined,
            query.erd_from,
            query.erd_to,
            query.cutoff_from,
            query.cutoff_to,
            query.excluded_customer_ids,
            query.assigned_user_ids,
            query.sort_by,
            query.sort_order,
            // page: `offset` carries the position instead, so the preload bite
            // can start partway into a page. The server takes `offset` over
            // `page` when both are present.
            undefined,
            pageSize,
            offset,
            includeTotal,
            { signal: abortController.signal }
          )
          .then((resp) => ({
            bookings: parseExports(resp.data.exports, useUserStore().demo_mode),
            total: resp.data.total ?? 0,
          }))
      const run = (
        offset: number,
        pageSize: number,
        isFirstBite: boolean
      ): Promise<void> =>
        fetchPage(offset, pageSize, isFirstBite)
          .then(({ bookings, total }) => {
            // Stale: a newer load owns `loading` and state. Do not clear here.
            if (generation !== this.loadGeneration) return
            // Second bite skips the count and sends 0. Keep the total from
            // the first ask so page buttons stay correct.
            if (isFirstBite || total > 0) {
              this.total = total
            }
            apply(bookings, isFirstBite)
            // Paint after the first 40, like Imports `preLoading`. `loading`
            // stays true until the rest of the page arrives so the toolbar can
            // show "Loading (40 of N)" — same as Imports ActionToolbar. On
            // Slow 3G that line lasts longer; that is waiting, not a stuck bug.
            if (isFirstBite) this.preLoading = false
            if (
              isFirstBite &&
              bookings.length === EXPORT_LIST_PRELOAD_SIZE &&
              total > offset + bookings.length
            ) {
              return run(
                offset + EXPORT_LIST_PRELOAD_SIZE,
                EXPORT_LIST_PAGE_SIZE - EXPORT_LIST_PRELOAD_SIZE,
                false
              )
            }
            this.loading = false
          })
          .catch((err) => {
            // A stop means a newer load owns this request.
            if (isRequestAbort(err)) {
              return
            }
            if (generation !== this.loadGeneration) return
            this.preLoading = false
            this.loading = false
            this.loadFailed = true
            throw err
          })
      // One finally for both bites (same controller). Identity check so a newer
      // load's controller is not cleared — same as containers.ts.
      return run(pageStart, EXPORT_LIST_PRELOAD_SIZE, true).finally(() => {
        if (this.loadAbortController === abortController) {
          this.loadAbortController = null
        }
      })
    },
    /**
     * Refresh one booking from the detail endpoint and merge it in.
     *
     * Carries the same fields as the list, appointments included, so this is a
     * refresh of one row rather than a way to fill anything in — see
     * `updateCustomer`, its only caller.
     */
    async loadOne(bookingNumber: string): Promise<void> {
      const resp = await new ExportsApi().getExportExportsBookingNumberGet(
        bookingNumber
      )
      this.mergeExports([resp.data], { appointmentsIncluded: true })
    },
    /**
     * Track bookings, one payload row per table row.
     *
     * Sends `bookings[]` rather than the older `booking_numbers`, so a mixed
     * paste carries a customer per booking; `customerId` is the default for the
     * rows that name none. The two forms are mutually exclusive server-side.
     *
     * Already-tracked bookings must be filtered out by the caller, not sent:
     * the endpoint documents `customer_id` as "updates the customer on bookings
     * that are already tracked", so re-sending one silently overwrites whatever
     * customer someone set on it.
     */
    async watchBookings(
      bookings: BookingToWatch[],
      { customerId }: { customerId?: number } = {}
    ): Promise<AxiosResponse<POSTWatchExportsResponse>> {
      this.executingWatch = true
      const api = new ExportsApi()
      try {
        const resp = await api.watchExportBookingsExportsWatchPost({
          bookings: bookings.map((booking) => ({
            booking_number: booking.bookingNumber,
            customer_id: booking.customerId,
          })),
          customer_id: customerId,
        })
        this.mergeExports(resp.data.exports, { appointmentsIncluded: false })
        return resp
      } finally {
        // Errors propagate to the caller (the global axios interceptor already
        // notifies); awaiting inline keeps the merge on the returned promise, so
        // no orphaned .then() chain is left to reject unhandled on API failure
        this.executingWatch = false
      }
    },
    /**
     * Track bookings from a spreadsheet, parsed server-side.
     *
     * Sends no default customer: the file's own `customer` column is the only
     * source, so a blank cell means no customer rather than a dialog setting.
     */
    async uploadFile(
      file: File
    ): Promise<AxiosResponse<POSTWatchExportsResponse>> {
      this.executingWatch = true
      const api = new ExportsApi()
      try {
        const resp =
          await api.watchExportBookingsViaUploadExportsWatchViaUploadPost(file)
        this.mergeExports(resp.data.exports, { appointmentsIncluded: false })
        return resp
      } finally {
        this.executingWatch = false
      }
    },
    /**
     * What the Add bookings table knows about each row before anything is
     * added: found / not found / invalid / not checked yet, the org's existing
     * tracking, and the containers on file.
     *
     * Advisory only — it creates no watches and always answers 200, so nothing
     * here may block a submit. `skipErrorHandling` keeps a failed check out of
     * the global error toast for the same reason.
     *
     * Chunked at `BOOKING_CHECK_CHUNK_SIZE` and issued in parallel; the results
     * are concatenated in request order, which is the order the endpoint
     * promises within a chunk and the order `Promise.all` preserves across
     * them, so the caller can zip them onto its rows positionally.
     *
     * A newer call aborts the one in flight, and the aborted call rejects with
     * axios's `CanceledError` — callers should ignore it via `axios.isCancel`
     * rather than showing the user a failure they did not cause.
     */
    async checkBookings(
      bookingNumbers: string[]
    ): Promise<GETExportBookingCheck[]> {
      this.cancelBookingChecks()
      if (!bookingNumbers.length) return []
      const controller = new AbortController()
      this.checkAbortController = markRaw(controller)

      const api = new ExportsApi()
      const chunks: string[][] = []
      for (
        let i = 0;
        i < bookingNumbers.length;
        i += BOOKING_CHECK_CHUNK_SIZE
      ) {
        chunks.push(bookingNumbers.slice(i, i + BOOKING_CHECK_CHUNK_SIZE))
      }

      try {
        const responses = await Promise.all(
          chunks.map((chunk) =>
            api.checkExportBookingsExportsBookingsCheckPost(
              { booking_numbers: chunk },
              { signal: controller.signal, skipErrorHandling: true }
            )
          )
        )
        return responses.flatMap((resp) => resp.data.bookings)
      } finally {
        if (this.checkAbortController === controller) {
          this.checkAbortController = null
        }
      }
    },
    /** Abort an in-flight pre-add check — on a newer check, or on dialog close. */
    cancelBookingChecks(): void {
      this.checkAbortController?.abort()
      this.checkAbortController = null
    },
    /**
     * Update a booking's customer via the booking-level PATCH.
     *
     * The endpoint returns only the booking numbers it updated, not the
     * resolved booking, so this refetches via `loadOne` rather than
     * reconstructing the `SavedCustomer` object locally from the id a picker
     * resolved — the server's copy (name included) is the one worth trusting.
     */
    async updateCustomer(
      bookingNumber: string,
      customerId: number
    ): Promise<void> {
      const api = new ExportsApi()
      await api.patchExportsExportsPatch({
        booking_numbers: [bookingNumber],
        customer_id: customerId,
      })
      await this.loadOne(bookingNumber)
    },
    /**
     * Set how many of each container type this org is responsible for on one
     * booking (DEV-8140/DEV-8182).
     *
     * The endpoint **merges**: only the keys sent are touched, and `null`
     * removes one. So callers send the types they changed, not the whole map —
     * which is also what keeps two tabs editing different types on one booking
     * from clobbering each other, since neither has to read-modify-write.
     * An empty `quantities` is a no-op server-side; it is refused here instead
     * of spending a round trip and a refetch to change nothing.
     *
     * One booking at a time, deliberately, even though `booking_numbers` takes
     * a list: a quantity is per booking, so applying one map across a
     * selection would write the same counts onto every booking in it.
     *
     * Refetched through `loadOne` for the same reason `updateCustomer` is —
     * the response carries only the booking numbers it touched. The PATCH also
     * publishes `export_updated`, so other open tabs converge on their own.
     */
    async updateCarrierQuantities(
      bookingNumber: string,
      quantities: Partial<Record<ContainerType, number | null>>
    ): Promise<void> {
      if (!Object.keys(quantities).length) return
      const api = new ExportsApi()
      await api.patchExportsExportsPatch({
        booking_numbers: [bookingNumber],
        // The spec declares the values nullable and the API documents `null` as
        // how a key is cleared, but the generator drops nullability off an
        // `additionalProperties` value — so the checked-in client types this
        // `number` and a clear won't typecheck without saying so here.
        carrier_quantities: quantities as { [key: string]: number },
      })
      await this.loadOne(bookingNumber)
    },
    async unwatchBookings(bookingNumbers: string[]): Promise<void> {
      this.executingUnwatch = true
      const api = new ExportsApi()
      try {
        // Errors propagate to the caller (the global axios interceptor already
        // notifies), leaving local state untouched
        await api.stopWatchingExportBookingsExportsUnwatchPost(bookingNumbers)
      } finally {
        this.executingUnwatch = false
      }
      let removed = 0
      for (const bookingNumber of bookingNumbers) {
        const index = this.exports.findIndex(
          (booking) => booking.booking_number === bookingNumber
        )
        if (index !== -1) {
          this.exports.splice(index, 1)
          removed += 1
        }
        this.exportLookup.delete(bookingNumber)
      }
      // Badge reads `total` when the last load returned a count. Drop with the
      // rows so archive does not leave "N bookings" stuck on the old total.
      if (removed > 0 && this.total > 0) {
        this.total = Math.max(0, this.total - removed)
      }
      Notification.success(
        identifierListNotification(
          `Archived ${bookingNumbers.length} export booking${bookingNumbers.length > 1 ? 's' : ''}`,
          bookingNumbers
        )
      )
    },
    /**
     * Merge fresh bookings in, replacing any copy already held.
     *
     * `appointmentsIncluded` says whether the payload's `appointments` are an
     * answer or an omission: the list, the detail endpoint and the websocket
     * push all carry them, but the watch/upload endpoints deliberately don't
     * (`include_appointments=False`). Re-adding an already-tracked booking goes
     * through watch, so without this its appointment rows would vanish from the
     * list until the next reload. Stated by the caller rather than inferred
     * from an empty array, which is also what a booking with no appointments
     * legitimately looks like.
     */
    mergeExports(
      raw: GETExport[],
      { appointmentsIncluded }: { appointmentsIncluded: boolean }
    ) {
      for (const booking of parseExports(raw, useUserStore().demo_mode)) {
        const existingIndex = this.exports.findIndex(
          (existing) => existing.booking_number === booking.booking_number
        )
        if (!appointmentsIncluded && existingIndex !== -1) {
          booking.appointments = this.exports[existingIndex].appointments
        }
        if (existingIndex === -1) {
          this.exports.push(booking)
        } else {
          this.exports[existingIndex] = booking
        }
        this.exportLookup.set(booking.booking_number, booking)
      }
      // Do not bump `total` here. Watch/upload go through
      // AddExportBookingsDialog → afterBookingsAdded → load, which sets total
      // from the server for the active filters. A client bump would count
      // bookings that the current filters exclude (and websocket ExportUpdated
      // inserts). Archive still decrements in unwatchBookings — that path has
      // no reload.
    },
  },
})
